<template>
  <div class="ts-tool">
    <!-- Mode selector -->
    <div class="ts-mode-bar">
      <el-radio-group v-model="mode" size="mini" @change="onModeChange">
        <el-radio-button label="direct">{{ $t('tool.TableStructure.directMode') }}</el-radio-button>
        <el-radio-button label="agent">{{ $t('tool.TableStructure.agentMode') }}</el-radio-button>
      </el-radio-group>
      <a v-if="mode==='agent'" :href="agentDownloadUrl" target="_blank" class="ts-agent-dl">
        <i class="el-icon-download" /> {{ $t('tool.TableStructure.downloadAgent') }}
      </a>
    </div>

    <!-- Agent help -->
    <div class="ts-agent-help" v-if="mode==='agent'">
      <i class="el-icon-info" /> {{ $t('tool.TableStructure.agentHelp') }}
    </div>

    <el-form ref="form" :model="conn" label-width="90px" size="small">
      <!-- Agent mode: sessionId -->
      <el-form-item v-if="mode==='agent'" :label="$t('tool.TableStructure.sessionId')" :rules="[{required:true,message:$t('tool.TableStructure.sessionIdRequired')}]" prop="sessionId">
        <el-input v-model="conn.sessionId" :placeholder="$t('tool.TableStructure.phSessionId')" />
      </el-form-item>

      <el-row :gutter="12">
        <el-col :span="16">
          <el-form-item :label="$t('tool.TableStructure.host')" :rules="[{required:true,message:$t('tool.TableStructure.hostRequired')}]" prop="host">
            <el-input v-model="conn.host" placeholder="127.0.0.1" />
          </el-form-item>
        </el-col>
        <el-col :span="8">
          <el-form-item :label="$t('tool.TableStructure.port')" prop="port">
            <el-input v-model="conn.port" placeholder="3306" />
          </el-form-item>
        </el-col>
      </el-row>
      <el-row :gutter="12">
        <el-col :span="12">
          <el-form-item :label="$t('tool.TableStructure.user')" :rules="[{required:true,message:$t('tool.TableStructure.userRequired')}]" prop="user">
            <el-input v-model="conn.user" placeholder="root" />
          </el-form-item>
        </el-col>
        <el-col :span="12">
          <el-form-item :label="$t('tool.TableStructure.password')" prop="password">
            <el-input v-model="conn.password" type="password" :placeholder="$t('tool.TableStructure.passwordPlaceholder')" />
          </el-form-item>
        </el-col>
      </el-row>
      <el-form-item :label="$t('tool.TableStructure.database')" :rules="[{required:true,message:$t('tool.TableStructure.databaseRequired')}]" prop="database">
        <el-input v-model="conn.database" placeholder="your_database" />
      </el-form-item>
      <el-form-item>
        <el-button type="primary" :loading="loading" @click="doExport">{{ $t('tool.TableStructure.btnExport') }}</el-button>
        <el-button @click="doClear">{{ $t("common.action.clear") }}</el-button>
      </el-form-item>
    </el-form>

    <!-- Error -->
    <div class="ts-card-err" v-if="error">
      <div class="ts-err">{{ error }}</div>
    </div>

    <div class="ts-tip">
      <p><i class="el-icon-info" /> {{ $t('tool.TableStructure.tipUsage') }}</p>
      <p><i class="el-icon-warning" /> {{ $t('tool.TableStructure.tipSecurity') }}</p>
    </div>
  </div>
</template>

<script>
import request from '@/api/request'

export default {
  name: 'TableStructureTool',
  props: { config: { type: Object, default: () => ({}) } },
  data() {
    return {
      mode: 'direct',
      conn: { host: '127.0.0.1', port: '3306', user: 'root', password: '', database: '', sessionId: '' },
      loading: false,
      error: '',
      ws: null
    }
  },
  computed: {
    agentDownloadUrl() {
      return 'https://onltool.site/static/agent.exe'
    }
  },
  beforeDestroy() {
    this.closeWs()
  },
  methods: {
    closeWs() {
      const old = this.ws
      if (old) { old.onclose = null; old.close() }
      this.ws = null
    },
    onModeChange() {
      this.closeWs(); this.error = ''
    },
    doExport() {
      this.error = ''
      if (this.mode === 'agent') {
        if (!this.conn.sessionId) {
          this.$message.warning(this.$t('tool.TableStructure.sessionIdRequired'))
          return
        }
        this.exportViaAgent()
      } else {
        this.$refs.form.validate(valid => {
          if (!valid) return
          this.exportDirect()
        })
      }
    },
    exportDirect() {
      this.loading = true
      request({
        url: '/api/pub/table-structure/export',
        method: 'post',
        data: this.conn,
        responseType: 'blob'
      }).then(res => {
        const url = window.URL.createObjectURL(new Blob([res]))
        const a = document.createElement('a')
        a.href = url
        a.download = this.conn.database + '_table_structure.doc'
        document.body.appendChild(a)
        a.click()
        document.body.removeChild(a)
        window.URL.revokeObjectURL(url)
        this.$message.success(this.$t('tool.TableStructure.exportSuccess'))
      }).catch(() => {
        this.error = this.$t('tool.TableStructure.exportFail')
      }).finally(() => { this.loading = false })
    },
    exportViaAgent() {
      this.closeWs()
      this.loading = true
      const protocol = location.protocol === 'https:' ? 'wss:' : 'ws:'
      const url = protocol + '//' + location.host + '/ws/browser/' + this.conn.sessionId

      const connectTimeout = setTimeout(() => {
        this.error = this.$t('tool.TableStructure.errAgentConnect')
        this.loading = false
        this.closeWs()
      }, 10000)

      let responseTimeout = null

      this.ws = new WebSocket(url)
      this.ws.onopen = () => {
        clearTimeout(connectTimeout)
        this.ws.send(JSON.stringify({
          type: 'tableStructure',
          host: this.conn.host,
          port: Number(this.conn.port) || 3306,
          user: this.conn.user,
          password: this.conn.password,
          database: this.conn.database
        }))
        responseTimeout = setTimeout(() => {
          this.error = this.$t('tool.TableStructure.errAgentConnect')
          this.loading = false
          this.closeWs()
        }, 30000)
      }
      this.ws.onmessage = (e) => {
        try {
          const msg = JSON.parse(e.data)
          clearTimeout(responseTimeout)
          this.handleAgentMsg(msg)
        } catch (ex) {}
      }
      this.ws.onerror = () => {
        clearTimeout(connectTimeout); clearTimeout(responseTimeout)
        this.error = this.$t('tool.TableStructure.errAgentConnect')
        this.loading = false
      }
      this.ws.onclose = () => {
        clearTimeout(responseTimeout)
        if (this.loading) {
          this.error = this.$t('tool.TableStructure.errAgentConnect')
          this.loading = false
        }
        this.ws = null
      }
    },
    handleAgentMsg(msg) {
      switch (msg.type) {
        case 'tableStructureResult':
          // Relay to backend for FreeMarker Word rendering
          request({
            url: '/api/pub/table-structure/export',
            method: 'post',
            data: { database: msg.database, tables: msg.tables },
            responseType: 'blob'
          }).then(res => {
            const url = window.URL.createObjectURL(new Blob([res]))
            const a = document.createElement('a')
            a.href = url
            a.download = (msg.database || 'database') + '_table_structure.doc'
            document.body.appendChild(a)
            a.click()
            document.body.removeChild(a)
            window.URL.revokeObjectURL(url)
            this.$message.success(this.$t('tool.TableStructure.exportSuccess'))
          }).catch(() => {
            this.error = this.$t('tool.TableStructure.exportFail')
          }).finally(() => { this.loading = false })
          break
        case 'error':
          this.error = msg.message || 'Unknown error'
          this.loading = false
          break
        case 'agentDisconnected':
          this.error = this.$t('tool.TableStructure.agentDisconnected')
          this.loading = false
          break
        default: break
      }
    },
    doClear() {
      this.conn = { host: '127.0.0.1', port: '3306', user: 'root', password: '', database: '', sessionId: this.conn.sessionId }
      this.error = ''
    },
    doAction(a) { if (a === 'clear') this.doClear() }
  }
}
</script>

<style scoped>
.ts-tool { width: 100%; }

/* Mode bar */
.ts-mode-bar { display: flex; align-items: center; gap: 12px; margin-bottom: 16px; }
.ts-agent-dl { font-size: 12px; color: #4e7cff; text-decoration: none; font-weight: 600; margin-left: auto; }
.ts-agent-dl:hover { text-decoration: underline; }
.ts-agent-dl i { margin-right: 3px; }
.ts-agent-help { font-size: 12px; color: #909399; background: #f0f9ff; border: 1px solid #d0e8ff; border-radius: 8px; padding: 8px 12px; margin-bottom: 14px; display: flex; align-items: center; gap: 6px; }
.ts-agent-help i { color: #4e7cff; flex-shrink: 0; }

/* Error */
.ts-card-err { background: #fff5f5; border: 1px solid #fde0e0; border-radius: 8px; padding: 12px 16px; margin-bottom: 16px; }
.ts-err { font-size: 13px; color: #f56c6c; }

.ts-tip {
  margin-top: 24px; padding: 14px 16px;
  background: #f8f9fb; border-radius: 8px; border: 1px solid #eef0f4;
}
.ts-tip p { margin: 0 0 6px; font-size: 12px; color: #8892a6; display: flex; align-items: flex-start; gap: 6px; }
.ts-tip p:last-child { margin-bottom: 0; color: #b0b8c4; }
.ts-tip i { color: #4e7cff; margin-top: 1px; }
</style>
